import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, of, Subject, Subscription } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap, takeUntil } from 'rxjs/operators';

import { CompareService } from '../../../core/providers/compare/compare.service';
import { DataService } from '../../../core/providers/data/data.service';
import { Dialog } from '../../../core/providers/modal/modal-types';
import { GET_PRODUCTS_BY_IDS, SEARCH_PRODUCTS } from '../../../core/components/product-list/product-list.graphql';

export interface CompareDialogAsset {
    preview: string;
    focalPoint?: { x: number; y: number } | null;
}

export interface CompareDialogProduct {
    id: string;
    name: string;
    slug: string;
    asset: CompareDialogAsset | null;
}

interface SearchResult {
    productId: string;
    productName: string;
    slug: string;
    productAsset?: CompareDialogAsset | null;
}

@Component({
    selector: 'vsf-compare-dialog',
    template: `
        <div class="fixed bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 z-50 w-[min(1200px,calc(100vw-16px))] sm:w-[min(1200px,calc(100vw-48px))]">
            <div class="bg-[#444445] rounded-lg shadow-2xl p-3 sm:p-4 flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3 sm:gap-4 relative">
                <button type="button" (click)="close()"
                        [attr.aria-label]="'compare.remove' | translate"
                        class="absolute -top-3 -right-3 w-8 h-8 bg-white rounded-full shadow text-body hover:text-primary transition-colors flex items-center justify-center">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                </button>

                <div class="flex flex-col lg:flex-row items-stretch lg:items-center gap-3 lg:gap-4 flex-1 min-w-0 w-full">
                    <!-- Slot 1 -->
                    <div *ngIf="firstProduct" class="bg-white rounded pl-1 pr-4 py-1 flex items-center gap-4 w-full lg:flex-1 lg:max-w-[400px] h-[88px] shrink-0">
                        <div class="w-20 h-20 rounded bg-surface flex items-center justify-center overflow-hidden shrink-0">
                            <img *ngIf="firstProduct.asset" [src]="firstProduct.asset | assetPreview:'thumb'"
                                 [alt]="firstProduct.name"
                                 class="max-w-full max-h-full object-contain"/>
                        </div>
                        <p class="flex-1 min-w-0 text-body text-lg font-semibold truncate">{{ firstProduct.name }}</p>
                        <button type="button" (click)="removeFirst()"
                                [attr.aria-label]="'compare.remove' | translate"
                                class="text-body hover:text-primary transition-colors shrink-0">
                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12"/>
                            </svg>
                        </button>
                    </div>

                    <!-- Slot 2 (filled) -->
                    <div *ngIf="secondProduct" class="bg-white rounded pl-1 pr-4 py-1 flex items-center gap-4 w-full lg:flex-1 lg:max-w-[400px] h-[88px] shrink-0">
                        <div class="w-20 h-20 rounded bg-surface flex items-center justify-center overflow-hidden shrink-0">
                            <img *ngIf="secondProduct.asset" [src]="secondProduct.asset | assetPreview:'thumb'"
                                 [alt]="secondProduct.name"
                                 class="max-w-full max-h-full object-contain"/>
                        </div>
                        <p class="flex-1 min-w-0 text-body text-lg font-semibold truncate">{{ secondProduct.name }}</p>
                        <button type="button" (click)="removeSecond()"
                                [attr.aria-label]="'compare.remove' | translate"
                                class="text-body hover:text-primary transition-colors shrink-0">
                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12"/>
                            </svg>
                        </button>
                    </div>

                    <!-- Slot 2 (search) -->
                    <div *ngIf="!secondProduct" class="bg-white rounded px-4 py-2 flex items-center gap-3 w-full lg:flex-1 lg:max-w-[400px] h-[88px] shrink-0 relative">
                        <svg class="w-5 h-5 text-disabled shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z"/>
                        </svg>
                        <input type="text"
                               [value]="searchTerm"
                               (input)="onSearchInput($any($event.target).value)"
                               [placeholder]="'compare.searchPlaceholder' | translate"
                               class="flex-1 bg-transparent text-body text-base focus:outline-none"/>

                        <!-- Results dropdown -->
                        <div *ngIf="searchTerm.length >= 1"
                             class="absolute left-0 right-0 bottom-full mb-2 rounded-lg border border-gray-200 bg-white shadow-xl max-h-80 overflow-y-auto z-10">
                            <div *ngIf="loading" class="p-4 text-disabled text-sm">{{ 'compare.searching' | translate }}</div>
                            <div *ngIf="!loading && results.length === 0" class="p-4 text-disabled text-sm">
                                {{ 'compare.noResults' | translate }}
                            </div>
                            <button type="button"
                                    *ngFor="let r of results"
                                    (click)="selectResult(r)"
                                    class="w-full flex items-center gap-4 px-4 py-3 border-b border-gray-100 last:border-b-0 hover:bg-surface transition-colors text-left">
                                <div class="w-12 h-12 rounded bg-surface flex items-center justify-center overflow-hidden shrink-0">
                                    <img *ngIf="r.productAsset"
                                         [src]="r.productAsset | assetPreview:'thumb'"
                                         [alt]="r.productName"
                                         class="max-w-full max-h-full object-contain"/>
                                </div>
                                <span class="flex-1 text-body text-sm font-medium truncate">{{ r.productName }}</span>
                            </button>
                        </div>
                    </div>
                </div>

                <button type="button"
                        (click)="submit()"
                        [disabled]="!firstProduct || !secondProduct"
                        class="bg-primary text-white h-[60px] rounded-lg px-6 font-semibold uppercase text-base flex items-center justify-center gap-3 w-full lg:w-auto shrink-0 hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                    {{ 'compare.compareBtn' | translate }}
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M13 5l7 7-7 7M5 12h15"/>
                    </svg>
                </button>
            </div>
        </div>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompareDialogComponent implements Dialog<void>, OnInit, OnDestroy {
    resolveWith: (result?: void) => void;

    firstProduct: CompareDialogProduct | null = null;
    secondProduct: CompareDialogProduct | null = null;

    searchTerm = '';
    results: SearchResult[] = [];
    loading = false;

    private readonly searchTerm$ = new BehaviorSubject<string>('');
    private readonly destroy$ = new Subject<void>();
    private sub: Subscription;

    constructor(
        private compareService: CompareService,
        private dataService: DataService,
        private router: Router,
        private cdr: ChangeDetectorRef,
    ) {}

    ngOnInit() {
        this.sub = this.searchTerm$
            .pipe(
                debounceTime(250),
                distinctUntilChanged(),
                switchMap(term => {
                    if (!term || term.length < 1) {
                        this.loading = false;
                        this.cdr.markForCheck();
                        return of<SearchResult[]>([]);
                    }
                    this.loading = true;
                    this.cdr.markForCheck();
                    return this.dataService
                        .query<{ search: { items: SearchResult[] } }>(SEARCH_PRODUCTS, {
                            input: { term, take: 8, groupByProduct: true },
                        })
                        .pipe(
                            switchMap(res => {
                                const items = res?.search?.items ?? [];
                                if (items.length === 0) return of<SearchResult[]>([]);
                                const ids = items.map(i => i.productId);
                                return this.dataService
                                    .queryFresh<{ products: { items: Array<{ id: string; name: string; slug: string }> } }>(
                                        GET_PRODUCTS_BY_IDS,
                                        { options: { filter: { id: { in: ids } }, take: ids.length } },
                                    )
                                    .pipe(
                                        switchMap(({ products }) => {
                                            const map = new Map(products.items.map(p => [p.id, p]));
                                            return of(items.map(i => {
                                                const localized = map.get(i.productId);
                                                return localized
                                                    ? { ...i, productName: localized.name, slug: localized.slug }
                                                    : i;
                                            }));
                                        }),
                                    );
                            }),
                            catchError(err => {
                                console.warn('[CompareDialog] search failed:', err);
                                return of<SearchResult[]>([]);
                            }),
                        );
                }),
                takeUntil(this.destroy$),
            )
            .subscribe(items => {
                const firstId = this.firstProduct?.id;
                this.results = firstId ? items.filter(i => i.productId !== firstId) : items;
                this.loading = false;
                this.cdr.markForCheck();
            });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
        this.sub?.unsubscribe();
    }

    onSearchInput(value: string) {
        this.searchTerm = value;
        this.searchTerm$.next(value);
    }

    selectResult(item: SearchResult) {
        this.secondProduct = {
            id: item.productId,
            name: item.productName,
            slug: item.slug,
            asset: item.productAsset ?? null,
        };
        if (!this.compareService.has(item.productId)) {
            this.compareService.toggle(item.productId);
        }
        this.searchTerm = '';
        this.searchTerm$.next('');
        this.results = [];
        this.cdr.markForCheck();
    }

    removeFirst() {
        if (this.firstProduct) {
            this.compareService.remove(this.firstProduct.id);
            this.firstProduct = null;
            this.cdr.markForCheck();
        }
        if (!this.firstProduct && !this.secondProduct) {
            this.close();
        }
    }

    removeSecond() {
        if (this.secondProduct) {
            this.compareService.remove(this.secondProduct.id);
            this.secondProduct = null;
            this.cdr.markForCheck();
        }
    }

    submit() {
        if (!this.firstProduct || !this.secondProduct) return;
        this.router.navigate(['/compare']);
        this.resolveWith();
    }

    close() {
        this.resolveWith();
    }
}
